// Shared matrix computation for outreach generator and public briefing page.
// Pure functions — no server-only imports.
import type { Stage, EntityStats, PromptType, StageCell } from "./outreach-flag";

const STAGES: Stage[] = ["discovery", "evaluation", "decision"];
const PROMPT_TYPES: PromptType[] = ["conceptual", "comparative"];

const norm = (s: string) => s.toLowerCase().trim();

export function extractDomain(url: string | null | undefined): string {
  if (!url) return "";
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return url.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0]?.toLowerCase() ?? "";
  }
}

// Generic corporate words that must never carry a match on their own —
// otherwise "Trilon Group" matches any mention containing "group" and every
// cell comes back near 100%.
const GENERIC_TOKENS = new Set([
  "the", "inc", "inc.", "llc", "ltd", "co", "corp", "corporation", "company",
  "group", "holdings", "partners", "technologies", "technology", "solutions",
  "software", "systems", "services", "labs", "global", "international",
  "consulting", "agency", "studio", "media", "digital", "health", "capital",
]);

const stripPunct = (s: string) => s.replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();

// Reduce a company name to its distinctive core, e.g.
// "Trilon Group, Inc." -> "trilon", "AECOM Technical Services" -> "aecom".
function coreName(name: string): string {
  const tokens = stripPunct(norm(name)).split(" ").filter(Boolean);
  const kept = tokens.filter((t) => !GENERIC_TOKENS.has(t));
  return (kept.length ? kept : tokens).join(" ");
}

// Whole-phrase match on word boundaries — no loose substring containment.
function mentions(haystack: string, core: string): boolean {
  if (!core || core.length < 3) return false;
  const h = ` ${stripPunct(haystack)} `;
  return h.includes(` ${core} `);
}

/** True when a stored answer's mentioned_entities name this company. Shared by every mention count. */
export function answerNames(mentionedEntities: unknown, name: string): boolean {
  // Competitors are often saved as a web address; reduce it to its words.
  const cleaned = name
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/.*$/, "")
    .replace(/\.(com|net|org|co|io|ai|us|realty|homes)$/i, "");
  const core = coreName(cleaned);
  const squashed = core.replace(/ /g, "");
  const me = Array.isArray(mentionedEntities) ? mentionedEntities : [];
  return me.some((m: unknown) => {
    const f =
      typeof m === "string"
        ? norm(m)
        : norm(String((m as any)?.name ?? (m as any)?.entity ?? JSON.stringify(m)));
    if (mentions(f, core)) return true;
    return squashed.length >= 6 && stripPunct(f).replace(/ /g, "").includes(squashed);
  });
}

function rootDomain(d: string): string {
  const parts = d.split(".").filter(Boolean);
  return parts.length > 2 ? parts.slice(-2).join(".") : d;
}

function scoreCell(list: any[], name: string, domain: string, isOwn: boolean): StageCell {
  const core = coreName(name);
  const root = domain ? rootDomain(domain) : "";
  let named = 0;
  let cited = 0;
  for (const src of list) {
    const me = Array.isArray(src.mentioned_entities) ? src.mentioned_entities : [];
    const flat = me.map((m: unknown) =>
      typeof m === "string"
        ? norm(m)
        : norm(String((m as any)?.name ?? (m as any)?.entity ?? JSON.stringify(m))),
    );
    if (flat.some((f: string) => mentions(f, core))) named++;
    if (isOwn) {
      if (src.company_cited_with_link) cited++;
    } else if (root) {
      const cs = Array.isArray(src.cited_sources) ? src.cited_sources : [];
      if (
        cs.some((c: any) => {
          if (typeof c?.domain !== "string") return false;
          const d = c.domain.toLowerCase().replace(/^www\./, "");
          return d === root || d.endsWith(`.${root}`);
        })
      ) {
        cited++;
      }
    }
  }

  const total = list.length;
  return {
    namedCount: named,
    citedCount: cited,
    total,
    namedRate: total ? named / total : 0,
    citedRate: total ? cited / total : 0,
  };
}

export function computeStats(
  entity: { id: string; name: string; website_url?: string | null },
  isOwn: boolean,
  sourcesByStage: Record<Stage, any[]>,
  sourcesByStageByType?: Record<Stage, Record<PromptType, any[]>>,
): EntityStats {
  const domain = extractDomain(entity.website_url ?? null);
  const perStage = {} as EntityStats["perStage"];
  for (const stage of STAGES) {
    perStage[stage] = scoreCell(sourcesByStage[stage], entity.name, domain, isOwn);
  }
  let perStageByType: EntityStats["perStageByType"];
  if (sourcesByStageByType) {
    perStageByType = {} as NonNullable<EntityStats["perStageByType"]>;
    for (const stage of STAGES) {
      perStageByType[stage] = {} as Record<PromptType, StageCell>;
      for (const t of PROMPT_TYPES) {
        perStageByType[stage][t] = scoreCell(
          sourcesByStageByType[stage][t] ?? [],
          entity.name,
          domain,
          isOwn,
        );
      }
    }
  }
  return { id: entity.id, name: entity.name, isOwn, perStage, perStageByType };
}

export async function loadScanMatrix(
  supabaseAdmin: any,
  opts: { scanBatchId: string; ownEntityId: string; competitorEntityIds?: string[] },

): Promise<
  | { ok: false; reason: string }
  | {
      ok: true;
      own: { id: string; name: string; website_url: string | null; user_id: string; category: string; created_at?: string | null };
      ownStats: EntityStats;
      competitors: Array<{ id: string; name: string }>;
      compStats: EntityStats[];
    }
> {
  const { prospectAnswersTable } = await import("./prospect-tables");
  const answersTable = await prospectAnswersTable(supabaseAdmin, opts.scanBatchId);
  const sourcesRes = await supabaseAdmin
    .from(answersTable)
    .select("prompt_id, mentioned_entities, cited_sources, company_cited_with_link")
    .eq("check_status", "answered")
    .eq("scan_batch_id", opts.scanBatchId)
    // exclude rewritten-question rows — suggestions only, never scoring
    .eq("is_variation", false);
  if (sourcesRes.error) return { ok: false, reason: sourcesRes.error.message };

  const ownRes = await supabaseAdmin
    .from("tracked_entities")
    .select("id, name, type, website_url, user_id, business_context, created_at")
    .eq("id", opts.ownEntityId)
    .maybeSingle();
  if (ownRes.error || !ownRes.data) return { ok: false, reason: "own_entity_not_found" };
  const own = ownRes.data;

  const prospectCompany =
    own.type === "own_company"
      ? own.name
      : ((own.business_context as any)?.prospect_company ?? own.name);
  const category =
    (own.business_context as any)?.prospect_category ??
    (own.business_context as any)?.category ??
    "your category";

  const selectCompetitors = () => supabaseAdmin
    .from("tracked_entities")
    .select("id, name, type, website_url, created_at")
    .eq("user_id", own.user_id)
    .filter("business_context->>is_prospect_scan", "eq", "true")
    .filter("business_context->>prospect_company", "eq", prospectCompany)
    .neq("id", own.id);

  // Explicit competitor set (used by the public demo, where the tracked
  // competitors are ordinary account rows rather than prospect-scan rows).
  let compsRes = opts.competitorEntityIds?.length
    ? await supabaseAdmin
        .from("tracked_entities")
        .select("id, name, type, website_url, created_at")
        .in("id", opts.competitorEntityIds)
    : await selectCompetitors()
        .filter("business_context->>prospect_scan_batch_id", "eq", opts.scanBatchId);

  if (!opts.competitorEntityIds?.length && !compsRes.error && (compsRes.data ?? []).length === 0) {
    compsRes = await selectCompetitors()
      .gte("created_at", new Date(new Date(own.created_at).getTime()).toISOString())
      .lte("created_at", new Date(new Date(own.created_at).getTime() + 60_000).toISOString());
  }
  if (compsRes.error) return { ok: false, reason: compsRes.error.message };

  const seenCompetitors = new Set<string>();
  const competitors = (compsRes.data ?? []).filter((c: any) => {
    const key = `${norm(c.name)}|${extractDomain(c.website_url)}`;
    if (seenCompetitors.has(key)) return false;
    seenCompetitors.add(key);
    return true;
  });

  const promptIds = Array.from(
    new Set((sourcesRes.data ?? []).map((s: any) => s.prompt_id).filter(Boolean)),
  );
  const promptStage = new Map<string, Stage>();
  const promptType = new Map<string, PromptType>();
  if (promptIds.length) {
    const fromNew = answersTable === "prospect_answers"
      ? ((await supabaseAdmin.from("prospect_prompts").select("id, journey_stage, prompt_type").in("id", promptIds)).data ?? [])
      : [];
    const got = new Set(fromNew.map((p: any) => p.id));
    const rest = promptIds.filter((id) => !got.has(id));
    const fromOld = rest.length
      ? ((await supabaseAdmin.from("tracked_prompts").select("id, journey_stage, prompt_type").in("id", rest)).data ?? [])
      : [];
    for (const p of [...fromNew, ...fromOld]) {
      promptStage.set(p.id, p.journey_stage as Stage);
      promptType.set(p.id, (p.prompt_type as PromptType) ?? "conceptual");
    }
  }

  const sourcesByStage: Record<Stage, any[]> = { discovery: [], evaluation: [], decision: [] };
  const sourcesByStageByType: Record<Stage, Record<PromptType, any[]>> = {
    discovery: { conceptual: [], comparative: [] },
    evaluation: { conceptual: [], comparative: [] },
    decision: { conceptual: [], comparative: [] },
  };
  for (const s of sourcesRes.data ?? []) {
    const st = s.prompt_id ? promptStage.get(s.prompt_id) : undefined;
    const tp = s.prompt_id ? promptType.get(s.prompt_id) ?? "conceptual" : "conceptual";
    // Scans from full accounts can include a fourth stage (retention) that this
    // three-stage matrix does not score — skip rather than crash on it.
    if (st && sourcesByStage[st]) {
      sourcesByStage[st].push(s);
      sourcesByStageByType[st][tp].push(s);
    }

  }

  const ownStats = computeStats(own, true, sourcesByStage, sourcesByStageByType);
  const compStats = competitors.map((c: any) => computeStats(c, false, sourcesByStage, sourcesByStageByType));

  return {
    ok: true,
    own: {
      id: own.id,
      // Display name is always the entity itself (e.g. "Ivalua"), not the
      // scan's prospect_company grouping field (which for competitor-type
      // rows would surface the scan owner's brand instead).
      name: own.name,
      website_url: own.website_url,
      user_id: own.user_id,
      created_at: own.created_at,
      category,
    },
    ownStats,
    competitors: competitors.map((c: any) => ({ id: c.id, name: c.name })),
    compStats,
  };
}
